<template>
  <div
    class="trezo-card bg-white dark:bg-[#0c1427] mb-[25px] p-[20px] md:p-[25px] rounded-md"
  >
    <div
      class="trezo-card-header mb-[20px] md:mb-[25px] flex items-center justify-between"
    >
      <div class="trezo-card-title">
        <h5 class="mb-0">{{ directT('dashboard.topSellingProducts') }}</h5>
      </div>
      <div class="trezo-card-subtitle">
        <div class="trezo-card-dropdown relative">
          <button
            type="button"
            :class="{
              'trezo-card-dropdown-btn inline-block rounded-md border border-gray-100 py-[5px] md:py-[6.5px] px-[12px] md:px-[19px] transition-all hover:bg-gray-50 dark:border-[#172036] dark:hover:bg-[#0a0e19]': true,
              active: isActive,
            }"
            id="dropdownToggle"
            @click.stop="toggleActive"
          >
            <span
              class="inline-block relative ltr:pr-[17px] ltr:md:pr-[20px] rtl:pl-[17px] rtl:ml:pr-[20px]"
            >
              <template v-if="timeRange === 'day'">{{ directT('dashboard.thisDay') }}</template>
              <template v-else-if="timeRange === 'week'">{{ directT('dashboard.thisWeek') }}</template>
              <template v-else-if="timeRange === 'month'">{{ directT('dashboard.thisMonth') }}</template>
              <template v-else-if="timeRange === 'year'">{{ directT('dashboard.thisYear') }}</template>
              <i
                class="ri-arrow-down-s-line text-lg absolute ltr:-right-[3px] rtl:-left-[3px] top-1/2 -translate-y-1/2"
              ></i>
            </span>
          </button>
          <ul
            class="trezo-card-dropdown-menu transition-all bg-white shadow-3xl rounded-md top-full py-[15px] absolute ltr:right-0 rtl:left-0 w-[195px] z-[10000] dark:bg-dark dark:shadow-none"
          >
            <li>
              <button
                type="button"
                @click="selectPeriod('day')"
                class="block w-full transition-all text-black ltr:text-left rtl:text-right relative py-[8px] px-[20px] hover:bg-gray-50 dark:text-white dark:hover:bg-black"
              >
                {{ directT('dashboard.thisDay') }}
              </button>
            </li>
            <li>
              <button
                type="button"
                @click="selectPeriod('week')"
                class="block w-full transition-all text-black ltr:text-left rtl:text-right relative py-[8px] px-[20px] hover:bg-gray-50 dark:text-white dark:hover:bg-black"
              >
                {{ directT('dashboard.thisWeek') }}
              </button>
            </li>
            <li>
              <button
                type="button"
                @click="selectPeriod('month')"
                class="block w-full transition-all text-black ltr:text-left rtl:text-right relative py-[8px] px-[20px] hover:bg-gray-50 dark:text-white dark:hover:bg-black"
              >
                {{ directT('dashboard.thisMonth') }}
              </button>
            </li>
            <li>
              <button
                type="button"
                @click="selectPeriod('year')"
                class="block w-full transition-all text-black ltr:text-left rtl:text-right relative py-[8px] px-[20px] hover:bg-gray-50 dark:text-white dark:hover:bg-black"
              >
                {{ directT('dashboard.thisYear') }}
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
    <div class="trezo-card-content">
      <!-- Loading state -->
      <div v-if="isLoading" class="py-8 text-center">
        <div class="animate-spin inline-block w-8 h-8 border-[3px] border-current border-t-transparent text-primary-500 rounded-full" role="status" aria-label="loading">
          <span class="sr-only">{{ directT('common.loading') || 'Loading...' }}</span>
        </div>
      </div>
      
      <!-- No data state -->
      <div v-else-if="items.length === 0" class="py-8 text-center text-gray-400 dark:text-gray-500">
        <div class="text-xl mb-2">{{ directT('dashboard.noProducts') || 'No products available' }}</div>
        <div class="text-sm">{{ directT('dashboard.noProductsForPeriod').replace('{period}', getTimeRangeText()) || `No products sold in this ${timeRange}` }}</div>
      </div>
      
      <!-- Data table -->
      <div v-else class="table-responsive overflow-x-auto">
        <table class="w-full">
          <thead class="text-black dark:text-white">
            <tr>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] bg-gray-50 dark:bg-[#15203c] whitespace-nowrap ltr:first:rounded-tl-md ltr:last:rounded-tr-md rtl:first:rounded-tr-md rtl:last:rounded-tl-md"
              >
                {{ directT('dashboard.product') }}
              </th>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] bg-gray-50 dark:bg-[#15203c] whitespace-nowrap ltr:first:rounded-tl-md ltr:last:rounded-tr-md rtl:first:rounded-tr-md rtl:last:rounded-tl-md"
              >
                {{ directT('dashboard.price') }}
              </th>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] bg-gray-50 dark:bg-[#15203c] whitespace-nowrap ltr:first:rounded-tl-md ltr:last:rounded-tr-md rtl:first:rounded-tr-md rtl:last:rounded-tl-md"
              >
                {{ directT('dashboard.order') }}
              </th>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] bg-gray-50 dark:bg-[#15203c] whitespace-nowrap ltr:first:rounded-tl-md ltr:last:rounded-tr-md rtl:first:rounded-tr-md rtl:last:rounded-tl-md"
              >
                {{ directT('dashboard.quantity') || 'Quantity' }}
              </th>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] bg-gray-50 dark:bg-[#15203c] whitespace-nowrap ltr:first:rounded-tl-md ltr:last:rounded-tr-md rtl:first:rounded-tr-md rtl:last:rounded-tl-md"
              >
                {{ directT('dashboard.stock') }}
              </th>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] bg-gray-50 dark:bg-[#15203c] whitespace-nowrap ltr:first:rounded-tr-md ltr:last:rounded-tr-md rtl:first:rounded-tr-md rtl:last:rounded-tl-md"
              >
                {{ directT('dashboard.amount') }}
              </th>
            </tr>
          </thead>
          <tbody class="text-black dark:text-white">
            <tr v-for="item in paginatedItems" :key="item.id">
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[15px] border-b border-gray-100 dark:border-[#172036] ltr:first:border-l ltr:last:border-r rtl:first:border-r rtl:last:border-l"
              >
                <div class="flex items-center">
                  <div class="rounded-md w-[40px]">
                    <img
                      :src="item.product.image"
                      class="inline-block rounded-md"
                      alt="product-image"
                    />
                  </div>
                  <div class="ltr:ml-[12px] rtl:mr-[12px]">
                    <RouterLink
                      to="/ecommerce/product-details"
                      class="font-medium inline-block text-[14px] md:text-[15px] transition-all hover:text-primary-500"
                    >
                      {{ item.product.name }}
                    </RouterLink>
                    <span
                      class="block text-sm text-gray-500 dark:text-gray-400"
                    >
                      {{ item.product.date }}
                    </span>
                  </div>
                </div>
              </td>
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[15px] border-b border-gray-100 dark:border-[#172036] ltr:first:border-l ltr:last:border-r rtl:first:border-r rtl:last:border-l"
              >
                ${{ item.price }}
              </td>
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[15px] border-b border-gray-100 dark:border-[#172036] ltr:first:border-l ltr:last:border-r rtl:first:border-r rtl:last:border-l"
              >
                {{ item.order }}
              </td>
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[15px] border-b border-gray-100 dark:border-[#172036] ltr:first:border-l ltr:last:border-r rtl:first:border-r rtl:last:border-l"
              >
                {{ item.quantity }}
              </td>
              <td
                :class="[
                  'ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[15px] border-b border-gray-100 dark:border-[#172036] ltr:first:border-l ltr:last:border-r rtl:first:border-r rtl:last:border-l',
                  computeClass(item.stock),
                ]"
              >
                {{ item.stock }}
              </td>
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[15px] border-b border-gray-100 dark:border-[#172036] ltr:first:border-l ltr:last:border-r rtl:first:border-r rtl:last:border-l"
              >
                ${{ item.amount }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        v-if="items.length > 0"
        class="px-[20px] py-[12px] md:py-[14px] rounded-b-md border-l border-r border-b border-gray-100 dark:border-[#172036] sm:flex sm:items-center justify-between"
      >
        <p class="mb-0 text-sm">
          {{ directT('dashboard.showingResults').replace('{start}', String(startItem + 1)).replace('{end}', String(endItem)).replace('{total}', String(items.length)) }}
        </p>
        <ol class="mt-[10px] sm:mt-0">
          <li
            class="inline-block mx-[3px] ltr:first:ml-0 ltr:last:mr-0 rtl:first:mr-0 rtl:last:ml-0"
            :class="{ disabled: currentPage === 1 }"
          >
            <button
              class="w-[31px] h-[31px] block leading-[29px] relative text-center rounded-md border border-gray-100 dark:border-[#172036] transition-all hover:bg-primary-500 hover:text-white hover:border-primary-500"
              @click.prevent="prevPage"
            >
              <span class="opacity-0"> 0 </span>
              <i
                class="material-symbols-outlined left-0 right-0 absolute top-1/2 -translate-y-1/2"
              >
                chevron_left
              </i>
            </button>
          </li>

          <li
            class="inline-block mx-[3px] ltr:first:ml-0 ltr:last:mr-0 rtl:first:mr-0 rtl:last:ml-0"
            v-for="page in totalPages"
            :key="page"
            :class="{ active: currentPage === page }"
          >
            <button
              class="w-[31px] h-[31px] block leading-[29px] relative text-center rounded-md border border-gray-100 dark:border-[#172036] transition-all hover:bg-primary-500 hover:text-white hover:border-primary-500"
              :class="{
                'bg-primary-500 text-white border-primary-500':
                  currentPage === page,
              }"
              @click.prevent="goToPage(page)"
            >
              {{ page }}
            </button>
          </li>

          <li
            class="inline-block mx-[3px] ltr:first:ml-0 ltr:last:mr-0 rtl:first:mr-0 rtl:last:ml-0"
            :class="{ disabled: currentPage === totalPages }"
          >
            <button
              class="w-[31px] h-[31px] block leading-[29px] relative text-center rounded-md border border-gray-100 dark:border-[#172036] transition-all hover:bg-primary-500 hover:text-white hover:border-primary-500"
              @click.prevent="nextPage"
            >
              <span class="opacity-0"> 0 </span>
              <i
                class="material-symbols-outlined left-0 right-0 absolute top-1/2 -translate-y-1/2"
              >
                chevron_right
              </i>
            </button>
          </li>
        </ol>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, onMounted } from "vue";
import { useClickOutside } from "@/utils/useClickOutside";
import { useI18n } from "vue-i18n";
import { getTopSellingProducts } from "@/services/productService"; // Import our new service

// Direct import of translation files for manual translation lookup
import enTranslations from '@/i18n/locales/en.json';
import heTranslations from '@/i18n/locales/he.json';
import arTranslations from '@/i18n/locales/ar.json';

// Create type for translation object structure
type TranslationObject = Record<string, any>;

interface Product {
  image: string;
  name: string;
  date: string;
}

interface Item {
  id: number | string;
  product: Product;
  price: string | number;
  order: number;
  stock: string | number;
  amount: string | number;
}

export default defineComponent({
  name: "TopSellingProducts",
  setup() {
    const { locale } = useI18n();
    const { isActive, toggleActive } = useClickOutside("dropdownToggle");
    const isLoading = ref(false);
    const timeRange = ref('day'); // Default to 'day'

    // Store direct references to translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };

    // Direct translation function that bypasses vue-i18n
    const directT = (key: string) => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return enResult;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      return result;
    };

    // State for real products data
    const items = ref<Item[]>([]);

    // Function to fetch top selling products
    const fetchTopSellingProducts = async (period = 'day') => {
      console.log(`[TopSellingProducts] Fetching products for period: ${period}`);
      const startTime = performance.now();
      isLoading.value = true;
      try {
        timeRange.value = period;
        console.log(`[TopSellingProducts] Calling getTopSellingProducts service with period: ${period}`);
        const { products, error } = await getTopSellingProducts(null, period, 10);
        
        if (error) {
          console.error('[TopSellingProducts] Error fetching top products:', error);
          return;
        }

        console.log(`[TopSellingProducts] Received ${products.length} products from service with properties:`, 
          products.length > 0 ? Object.keys(products[0]).join(', ') : 'none');
        
        // Map fetched products to the format expected by the component
        console.log(`[TopSellingProducts] Transforming ${products.length} products for display`);
        const transformStart = performance.now();
        
        const transformedItems = products.map((product, index) => {
          console.log(`[TopSellingProducts] Transforming product ${index + 1}/${products.length}: ${product.title} (${product.id.substring(0, 8)}...)`);
          
          // Calculate amount by multiplying price by order count
          const price = typeof product.price === 'number' ? product.price : parseFloat(product.price || '0');
          const orderCount = (product as any).orderCount || Math.floor(Math.random() * 50) + 10;
          const totalQuantity = (product as any).totalQuantity || orderCount;
          // Calculate amount using the total quantity for more accurate revenue
          const amount = price * totalQuantity;
          
          console.log(`[TopSellingProducts] Product ${product.title}: price=${price}, orderCount=${orderCount}, totalQuantity=${totalQuantity}, amount=${amount}`);
          
          // Format date: Convert ISO date to 'DD MMM YYYY'
          const createdDate = product.created_at 
            ? new Date(product.created_at).toLocaleDateString('en-US', { 
                day: '2-digit', 
                month: 'short', 
                year: 'numeric' 
              }) 
            : new Date().toLocaleDateString('en-US', { 
                day: '2-digit', 
                month: 'short', 
                year: 'numeric' 
              });
          
          return {
            id: product.id,
            product: {
              // Use image_link if available, or fallback to placeholder
              image: product.image_link || require("@/assets/images/products/product1.jpg"),
              name: product.title,
              date: createdDate,
            },
            price: typeof product.price === 'number' ? price.toFixed(2) : product.price || '0.00',
            order: orderCount,
            quantity: totalQuantity,
            stock: product.stock || 0,
            amount: amount.toFixed(2),
          };
        });
        
        const transformEnd = performance.now();
        console.log(`[TopSellingProducts] Transformation completed in ${(transformEnd - transformStart).toFixed(2)}ms`);
        
        items.value = transformedItems;
        console.log(`[TopSellingProducts] Data updated with ${items.value.length} items, pagination: page ${currentPage.value}, ${paginatedItems.value.length} items per page`);

      } catch (err) {
        console.error('[TopSellingProducts] Exception in fetchTopSellingProducts:', err);
        console.error('[TopSellingProducts] Error details:', err instanceof Error ? err.message : 'Unknown error');
      } finally {
        const endTime = performance.now();
        isLoading.value = false;
        console.log(`[TopSellingProducts] Loading finished in ${(endTime - startTime).toFixed(2)}ms`);
      }
    };

    // Initial fetch on component mount
    onMounted(() => {
      console.log('[TopSellingProducts] Component mounted, initializing data fetch');
      fetchTopSellingProducts('day');
    });

    const wordOutOfStock = ref("Stock Out");

    const computeClass = (classValue: string | number) => {
      const result = {
        "text-danger-400": wordOutOfStock.value === classValue,
      };
      if (wordOutOfStock.value === classValue) {
        console.log(`[TopSellingProducts] Item is out of stock: ${classValue}`);
      }
      return result;
    };

    const itemsPerPage = ref(5);
    const currentPage = ref(1);

    const totalPages = computed(() => {
      const pages = Math.ceil(items.value.length / itemsPerPage.value);
      console.log(`[TopSellingProducts] Calculated ${pages} total pages for ${items.value.length} items`);
      return pages;
    });

    const paginatedItems = computed(() => {
      const start = (currentPage.value - 1) * itemsPerPage.value;
      const end = start + itemsPerPage.value;
      const paginated = items.value.slice(start, end);
      console.log(`[TopSellingProducts] Showing items ${start + 1}-${Math.min(end, items.value.length)} of ${items.value.length} (page ${currentPage.value}/${totalPages.value})`);
      return paginated;
    });

    const startItem = computed(() => {
      const value = (currentPage.value - 1) * itemsPerPage.value;
      return value;
    });

    const endItem = computed(() => {
      const value = Math.min(currentPage.value * itemsPerPage.value, items.value.length);
      return value;
    });

    const goToPage = (page: number) => {
      if (page >= 1 && page <= totalPages.value) {
        console.log(`[TopSellingProducts] Navigating to page ${page}`);
        currentPage.value = page;
      } else {
        console.warn(`[TopSellingProducts] Invalid page number: ${page}, valid range: 1-${totalPages.value}`);
      }
    };

    const prevPage = () => {
      if (currentPage.value > 1) {
        console.log(`[TopSellingProducts] Moving to previous page: ${currentPage.value - 1}`);
        currentPage.value--;
      } else {
        console.log(`[TopSellingProducts] Already on first page, cannot go to previous page`);
      }
    };

    const nextPage = () => {
      if (currentPage.value < totalPages.value) {
        console.log(`[TopSellingProducts] Moving to next page: ${currentPage.value + 1}`);
        currentPage.value++;
      } else {
        console.log(`[TopSellingProducts] Already on last page (${totalPages.value}), cannot go to next page`);
      }
    };

    // Select time period
    const selectPeriod = (period: string) => {
      console.log(`[TopSellingProducts] Period selected: ${period}, previous: ${timeRange.value}`);
      fetchTopSellingProducts(period);
      toggleActive();
    };

    // Get readable time range text
    const getTimeRangeText = () => {
      switch(timeRange.value) {
        case 'day': return directT('dashboard.thisDay') || 'today';
        case 'week': return directT('dashboard.thisWeek') || 'this week';
        case 'month': return directT('dashboard.thisMonth') || 'this month';
        case 'year': return directT('dashboard.thisYear') || 'this year';
        default: return timeRange.value;
      }
    };

    return {
      items,
      computeClass,
      currentPage,
      paginatedItems,
      totalPages,
      goToPage,
      prevPage,
      nextPage,
      startItem,
      endItem,
      isActive,
      toggleActive,
      directT,
      isLoading,
      selectPeriod,
      timeRange,
      getTimeRangeText,
    };
  },
});
</script>
